import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';

import { useTheme } from '@/contexts/ThemeContext';

type Props = {
  active: boolean;
  size?: number;
};

/** Επεκτεινόμενοι, ξεθωριασμένοι δακτύλιοι γύρω από ένα σημείο — οπτικό feedback
 * "σκαναρίσματος" όσο τρέχει μια αναζήτηση/φιλτράρισμα. */
export function SonarPulse({ active, size = 64 }: Props) {
  const { colors } = useTheme();
  const ring1 = useRef(new Animated.Value(0)).current;
  const ring2 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active) {
      ring1.stopAnimation();
      ring2.stopAnimation();
      ring1.setValue(0);
      ring2.setValue(0);
      return;
    }
    const makeLoop = (val: Animated.Value, delay: number) => Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(val, { toValue: 1, duration: 1200, easing: Easing.out(Easing.ease), useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(val, { toValue: 0, duration: 0, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    const loop1 = makeLoop(ring1, 0);
    const loop2 = makeLoop(ring2, 600);
    loop1.start();
    loop2.start();
    return () => { loop1.stop(); loop2.stop(); };
  }, [active, ring1, ring2]);

  if (!active) return null;

  return (
    <View pointerEvents="none" style={[styles.wrap, { width: size, height: size }]}>
      {[ring1, ring2].map((val, i) => (
        <Animated.View
          key={i}
          style={[
            styles.ring,
            {
              borderColor: colors.primary,
              opacity: val.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0] }),
              transform: [{ scale: val.interpolate({ inputRange: [0, 1], outputRange: [0.3, 1] }) }],
            },
          ]}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: 999,
    borderWidth: 1.5,
  },
});
